// frontend/modules/template/hooks/useScriptTesting.ts
import { useMutation } from '@tanstack/react-query';
import { 
    useExecuteScriptContent,
    useValidateScriptSyntax 
} from '@/shared/api/client/context-script-controller';
import { useContextBuilderStore } from '../stores/context-builder-store';
import { message } from 'antd';
import { ContextTransformationResult, ExecuteScriptContentRequest, ValidationResult, ScriptSyntaxValidationRequest} from "@/shared/api/model";
import {ContextScriptTestRequest} from "@/modules/template/types/context-transformation.types";

export const useScriptTesting = () => {
    const {
        validationResult,
        isValidating,
        setValidationResult,
        setIsValidating
    } = useContextBuilderStore();
    
    // API hooks
    // Note: useTestScript was replaced with useExecuteScriptContent after backend refactor
    const { mutateAsync: executeScriptApi } = useExecuteScriptContent();
    const { mutateAsync: validateScriptApi } = useValidateScriptSyntax();
    
    // Test script mutation
    const { mutateAsync: testScript, isPending: isTesting } = useMutation({
        mutationFn: (request: ContextScriptTestRequest) => {
            // Ensure both required fields are present
            if (!request.scriptContent || !request.testContextJson) {
                throw new Error('Script content and test context are required');
            }
            
            // Convert to the ExecuteScriptContentRequest format that the backend expects
            const executeRequest: ExecuteScriptContentRequest = {
                scriptContent: request.scriptContent,
                contextJson: request.testContextJson
            };
            
            return executeScriptApi({ data: executeRequest });
        },
        onSuccess: (response) => {
            return response as ContextTransformationResult;
        },
        onError: (err) => {
            console.error('Error testing script:', err);
            message.error('Failed to test script');
            throw err;
        }
    });
    
    // Validate script mutation
    const { mutateAsync: validateScript } = useMutation({
        mutationFn: (request: ContextScriptTestRequest) => {
            setIsValidating(true);
            
            // Create a proper validation request using ScriptSyntaxValidationRequest format
            const validationRequest: ScriptSyntaxValidationRequest = {
                scriptContent: request?.scriptContent || '',
                contextJson: request?.testContextJson || '{}'
            };
            
            return validateScriptApi({ 
                data: validationRequest
            });
        },
        onSuccess: (response) => {
            const result = response as ValidationResult;
            setValidationResult(result);
            
            if (result.valid) {
                message.success('Script is valid');
            } else {
                message.error(`Script has errors: ${result.message}`);
            }
            
            setIsValidating(false);
            return result;
        },
        onError: (err) => {
            console.error('Error validating script:', err);
            message.error('Failed to validate script');
            setIsValidating(false);
            
            // Create a failed validation result
            const result: ValidationResult = {
                valid: false,
                message: 'Validation request failed'
            };
            setValidationResult(result);
            return result;
        }
    });
    
    return {
        validationResult,
        isValidating,
        isTesting,
        testScript,
        validateScript
    };
};
